import React, { forwardRef, useEffect } from "react";
import HelpCard from "../HelpCard";
import { useSelector, useDispatch } from "react-redux";
import { openModal } from "../../../redux/actions/action-creator";
import { useLocation } from "react-router-dom";

const VoucherHelp = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { voucher_type_data = null } = useSelector((state) => state.common);
  const { userData = null } = useSelector((state) => state.auth);
  const isVoucherViewScreen = location.pathname.includes(
    "/voucher-transaction-view/",
  );

  const hasPermission = React.useCallback(
    (voucherType) => {
      if (
        userData?.login_type === "user" &&
        Array.isArray(userData?.permissions)
      ) {
        const pagePermissions = userData.permissions.find(
          (p) =>
            p.name.toLowerCase() === voucherType.toLowerCase() &&
            p.category.toLowerCase() === "voucher types",
        );
        return pagePermissions ? pagePermissions.create : false;
      }
      return true; // Admin or Owner
    },
    [userData],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      // On view screen, only allow: Ctrl+P (print), Ctrl+E (export), Alt+Q (edit log)
      // Block all voucher creation/alter shortcuts
      if (isVoucherViewScreen) {
        if (e.ctrlKey && (e.key === "p" || e.key === "P")) {
          e.preventDefault();
          dispatch(openModal("print"));
        } else if (e.ctrlKey && (e.key === "e" || e.key === "E")) {
          e.preventDefault();
          dispatch(openModal("export"));
        } else if (e.key === "Q" && e.altKey) {
          e.preventDefault();
          dispatch(openModal("editLog"));
        }
        return;
      }

      if (e.key === "F2") {
        e.preventDefault();
        if (userData?.allowed_back_dated !== "No") {
          dispatch(openModal("period"));
        }
      } else if (e.key === "F4" && hasPermission("Contra")) {
        e.preventDefault();
        props.displayNameOfVoucherType("Contra", voucher_type_data);
      } else if (e.key === "F5") {
        if (e.altKey && hasPermission("Debit Note")) {
          e.preventDefault();
          props.displayNameOfVoucherType("Debit Note", voucher_type_data);
        } else if (!e.altKey && hasPermission("Payment")) {
          e.preventDefault();
          props.displayNameOfVoucherType("Payment", voucher_type_data);
        }
      } else if (e.key === "F6") {
        if (e.altKey && hasPermission("Credit Note")) {
          e.preventDefault();
          props.displayNameOfVoucherType("Credit Note", voucher_type_data);
        } else if (!e.altKey && hasPermission("Receipt")) {
          e.preventDefault();
          props.displayNameOfVoucherType("Receipt", voucher_type_data);
        }
      } else if (e.key === "F7" && hasPermission("Journal")) {
        e.preventDefault();
        props.displayNameOfVoucherType("Journal", voucher_type_data);
      } else if (e.key === "F8" && hasPermission("Sales")) {
        e.preventDefault();
        props.displayNameOfVoucherType("Sales", voucher_type_data);
      } else if (e.key === "F9" && hasPermission("Purchase")) {
        e.preventDefault();
        props.displayNameOfVoucherType("Purchase", voucher_type_data);
      } else if (e.key === "F10") {
        e.preventDefault();
        dispatch(openModal("otherVoucher"));
      } else if (e.key === "F11") {
        e.preventDefault();
        dispatch(openModal("changeMode"));
      } else if (e.key === "F12") {
        e.preventDefault();
        dispatch(openModal("configure"));
      } else if (e.key === "L" && e.ctrlKey) {
        e.preventDefault();
        dispatch(openModal("optionalRegular"));
      } else if (e.key === "Q" && e.altKey) {
        e.preventDefault();
        dispatch(openModal("editLog"));
      } else if (e.ctrlKey && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        dispatch(openModal("print"));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    voucher_type_data,
    props.displayNameOfVoucherType,
    hasPermission,
    isVoucherViewScreen,
  ]);

  const fields = isVoucherViewScreen
    ? []
    : [
        {
          id: "1",
          name: "Date",
          shortcutKey: "F2",
          state: () => {
            if (userData?.allowed_back_dated !== "No")
              dispatch(openModal("period"));
          },
        },
        ...(hasPermission("Contra")
          ? [
              {
                id: "2",
                name: "Contra",
                shortcutKey: "F4",
                state: () =>
                  props.displayNameOfVoucherType("Contra", voucher_type_data),
              },
            ]
          : []),
        ...(hasPermission("Payment")
          ? [
              {
                id: "3",
                name: "Payment",
                shortcutKey: "F5",
                state: () =>
                  props.displayNameOfVoucherType("Payment", voucher_type_data),
              },
            ]
          : []),
        ...(hasPermission("Receipt")
          ? [
              {
                id: "4",
                name: "Receipt",
                shortcutKey: "F6",
                state: () =>
                  props.displayNameOfVoucherType("Receipt", voucher_type_data),
              },
            ]
          : []),
        ...(hasPermission("Journal")
          ? [
              {
                id: "5",
                name: "Journal",
                shortcutKey: "F7",
                state: () =>
                  props.displayNameOfVoucherType("Journal", voucher_type_data),
              },
            ]
          : []),
        ...(hasPermission("Sales")
          ? [
              {
                id: "6",
                name: "Sales",
                shortcutKey: "F8",
                state: () =>
                  props.displayNameOfVoucherType("Sales", voucher_type_data),
              },
            ]
          : []),
        ...(hasPermission("Purchase")
          ? [
              {
                id: "7",
                name: "Purchase",
                shortcutKey: "F9",
                state: () =>
                  props.displayNameOfVoucherType("Purchase", voucher_type_data),
              },
            ]
          : []),
        {
          id: "8",
          name: "Other Voucher",
          shortcutKey: "F10",
          state: () => dispatch(openModal("otherVoucher")),
        },
        {
          id: "9",
          name: "Change Mode",
          shortcutKey: "H",
          state: () => dispatch(openModal("changeMode")),
        },
        {
          id: "10",
          name: "Optional / Regular",
          shortcutKey: "L",
          underline: "double",
          state: () => dispatch(openModal("optionalRegular")),
        },
        {
          id: "11",
          name: "Print",
          shortcutKey: "Ctrl+P",
          state: () => dispatch(openModal("print")),
        },
        {
          id: "12",
          name: "Edit Log",
          shortcutKey: "Alt+Q",
          state: () => dispatch(openModal("editLog")),
        },
      ];
  return (
    <>
      {fields.map((item, index) => {
        return <HelpCard key={item.id} fields={item} />;
      })}
      {isVoucherViewScreen && (
        <div style={{ position: "absolute", bottom: 0, width: "100%" }}>
          <div className="help_button_card mb-1 ms-1">
            <button
              className={`button_padding`}
              onClick={() => dispatch(openModal("export"))}
              style={{
                background: "white",
                paddingTop: 3,
                paddingBottom: 3,
                marginBottom: 2,
              }}
            >
              <p className="card-header">
                <span>
                  <u className="double">E</u>
                </span>
                : Export
              </p>
            </button>
          </div>
          <div className="help_button_card mb-1 ms-1">
            <button
              className={`button_padding`}
              onClick={() => dispatch(openModal("print"))}
              style={{
                background: "white",
                paddingTop: 3,
                paddingBottom: 3,
                marginBottom: 2,
              }}
            >
              <p className="card-header">
                <span>
                  <u className="double">P</u>
                </span>
                : Print
              </p>
            </button>
          </div>
          <div className="help_button_card mb-1 ms-1">
            <button
              className={`button_padding`}
              onClick={() => dispatch(openModal("editLog"))}
              style={{
                background: "white",
                paddingTop: 3,
                paddingBottom: 3,
                marginBottom: 2,
              }}
            >
              <p className="card-header">
                <span>
                  <u className="single">Q</u>
                </span>
                : Edit Log
              </p>
            </button>
          </div>
        </div>
      )}
      {!isVoucherViewScreen && (
        <div style={{ position: "absolute", bottom: 0, width: "100%" }}>
          <div className="help_button_card mb-1 ms-1">
            <button
              className={`button_padding`}
              style={{
                background: "white",
                paddingTop: 3,
                paddingBottom: 3,
                marginBottom: 2,
              }}
            >
              <p className="card-header">
                <span>F12</span>: Configure
              </p>
            </button>
          </div>
        </div>
      )}
    </>
  );
});

export default VoucherHelp;
